import React, { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";

export const AthleteWarmupRoutinePhaseExercises = () => {
  const { athlete } = useAthleteAuthContext();
  const { product_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const phase = location.state?.phase;
  const productFromState = location.state?.product_basic;

  const exercises = phase?.exercises || [];

  //console.log(exercises);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState("");
  const [product, setProduct] = useState(productFromState || null);

  // Load product info for header
  useEffect(() => {
    if (productFromState) {
      setProduct(productFromState);
      return; // ❗ prevents API call
    }

    if (!athlete || !product_id) return;

    const loadProduct = async () => {
      try {
        const res = await fetch(
          `${API_URL.FRONTEND_ATHLETE_PURCHASED_SPEED_PRODUCT_DETAIL}${athlete.id}/${product_id}`,
          {
            headers: {
              Authorization: `Bearer ${athlete.token}`,
            },
          },
        );

        const json = await res.json();
        setProduct(json.data);
      } catch (err) {
        console.error("Product load failed:", err);
      }
    };

    loadProduct();
  }, [athlete, product_id]);

  const handleBackClick = () => {
    navigate("/athlete/speed-product/warmup-routine-phases/" + product_id, {
      state: { product_basic: product },
    });
  };

  const openVideoModal = (videoUrl) => {
    setSelectedVideo(videoUrl);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedVideo("");
    setModalOpen(false);
  };

  const handleAmaxSpeedWorkoutClick = () => {
    navigate(`/athlete/speed-product/speed-workout/${product_id}`, {
      state: {
        speedTrainingTemplate: product.speed_training_template,
        product_basic: product, // for back navigation
      },
    });
  };

  // Utility: Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;

    try {
      const u = new URL(url);

      // Shorts
      if (u.pathname.startsWith("/shorts/")) {
        return u.pathname.replace("/shorts/", "").split("?")[0];
      }

      // Regular watch URL
      if (u.searchParams.get("v")) {
        return u.searchParams.get("v");
      }

      // share links youtu.be/xyz
      if (u.hostname === "youtu.be") {
        return u.pathname.slice(1);
      }

      return null;
    } catch (e) {
      return null;
    }
  };

  // Utility: Convert to embed URL
  const getEmbedUrl = (url) => {
    const id = getYouTubeId(url);
    return id ? `https://www.youtube.com/embed/${id}` : url;
  };

  // Utility: Thumbnail
  const getThumbnail = (url) => {
    const id = getYouTubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
  };

  return (
    <AthleteLayout>
      {/*<div className="bg-white">
        <AthleteHeader />
      </div>
*/}

      {/* PRODUCT TITLE */}
      <div className="bg-white h-full">
        <div className="pb-3 mb-3 w-full max-w-2xl mx-auto flex flex-row justify-center items-center gap-10 px-5">
          <p className="w-full py-3 text-black text-xl line-clamp-2 leading-tight text-center border-b border-black/30">
            {product?.title || "Product"}
          </p>
        </div>

        <div className="w-full max-w-2xl mx-auto h-full overflow-y-auto">
          <div className="px-5 mb-4">
            {/* TABS */}
            <div className="bg-[#f5f4f4] rounded-2xl flex justify-between gap-8 px-4 py-4">
              <button className="text-black text-sm xs:text-base">
                Warmup
              </button>
              <button
                onClick={handleAmaxSpeedWorkoutClick}
                className="text-lightSmock text-sm xs:text-base"
              >
                Workout
              </button>
            </div>

            {/* PHASE HEADER */}
            <div className="w-full py-5 text-center relative">
              <div className="rounded-xl bg-black flex items-center gap-3 p-3">
                <span className="relative">
                  <ArrowLWhite onClick={handleBackClick} />
                </span>

                <p className="text-white text-base capitalize">
                  {phase?.title}
                </p>
              </div>
            </div>

            {/* REST PHASE UI */}
            {phase?.type === "RestPhase" && (
              <div className="px-5 mt-4 mb-4">
                <div className="rounded-xl p-4 border border-black/90">
                  <p className="text-black text-lg mb-2">Rest Phase</p>
                  <p className="text-black text-sm font-medium">
                    <strong>Duration:</strong> {phase.rest_duration || "—"}
                  </p>
                  <p className="text-black text-sm font-medium mt-1">
                    <strong>Notes:</strong> {phase.rest_notes || "—"}
                  </p>
                </div>
              </div>
            )}

            <div className="mb-5">
              <div className="bg-black rounded-xl py-3 px-3 xs:pr-8 divide-x-2 divide-white/50 flex flex-row justify-between items-stretch text-center relative z-10">
                <div className="flex-1 px-2">
                  <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                    Sets
                  </p>
                </div>
                <div className="flex-1 px-2">
                  <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                    Reps
                  </p>
                </div>
                <div className="flex-1 px-2">
                  <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                    Load/Time
                  </p>
                </div>
                <div className="flex-1 px-2">
                  <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                    Distance
                  </p>
                </div>
              </div>
            </div>

            {/* EX LIST */}
            <div className="h-full overflow-hidden overflow-y-auto xs:px-5">
              {exercises.length > 0 ? (
                exercises.map((item, index) => {
                  console.log(item);
                  const exercise = item.exercise_id;
                  const embedUrl = getEmbedUrl(exercise?.video_url);
                  const thumb = getThumbnail(exercise?.video_url);

                  return (
                    <div
                      key={index}
                      className="flex flex-col space-y-2 py-4 border-b-2 border-darkYellow cursor-pointer"
                      onClick={() => embedUrl && openVideoModal(embedUrl)}
                    >
                      {/* Header: Title + Thumbnail */}
                      <div className="flex items-start gap-3 xs:gap-5">
                        <div className="h-10 w-10 xs:h-14 xs:w-14">
                          {thumb && (
                            <img
                              src={thumb}
                              alt="video"
                              className="h-10 w-10 xs:h-14 xs:w-14 aspect-square object-cover"
                            />
                          )}
                        </div>
                        <div>
                          <p className="text-black text-base xs:text-lg font-bold capitalize">
                            {exercise?.title || "Untitled Exercise"}
                          </p>
                          {/* Details: Sets, Reps, Time, Distance, Rest Notes */}
                          <div className="flex flex-row flex-wrap items-stretch text-center cursor-pointer font-medium text-xs xs:text-base text-grayish">
                            {item.sets ? (
                              <p>
                                <span className="whitespace-nowrap">
                                  {item.sets} Sets x&nbsp;
                                </span>
                              </p>
                            ) : (
                              <p>
                                <span className="whitespace-nowrap">
                                  0 Sets x&nbsp;
                                </span>
                              </p>
                            )}
                            {item.reps ? (
                              <p>
                                <span className="whitespace-nowrap">
                                  {item.reps} Reps, &nbsp;
                                </span>
                              </p>
                            ) : (
                              <p>
                                <span className="whitespace-nowrap">
                                  0 Reps, &nbsp;
                                </span>
                              </p>
                            )}
                            {item.time && item.time != "None" ? (
                              <p>
                                <span className="whitespace-nowrap">
                                  {item.time} Time, &nbsp;
                                </span>
                              </p>
                            ) : (
                              <p>
                                <span className="whitespace-nowrap">
                                  0 Time, &nbsp;
                                </span>
                              </p>
                            )}
                            {item.distance ? (
                              <p>
                                <span className="whitespace-nowrap">
                                  {item.distance} Distance, &nbsp;
                                </span>
                              </p>
                            ) : (
                              <p>
                                <span className="whitespace-nowrap">
                                  0 Distance, &nbsp;
                                </span>
                              </p>
                            )}
                            {item.rest_notes && (
                              <p className="whitespace-nowrap">
                                <span className="italic text-base font-bold">
                                  {item.rest_notes} &nbsp;
                                </span>
                                <span>ßRest,</span>
                              </p>
                            )}
                          </div>
                          {/* Notes (if any) */}
                          {item.notes && (
                            <div className="mt-1">
                              <span className="text-black/80 font-medium text-xs xs:text-sm">
                                Notes:
                              </span>
                              <p className="text-grayish font-medium text-xs xs:text-sm ">
                                {item.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-black text-center mt-5">
                  No exercises found.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* VIDEO MODAL */}
        {/* VIDEO MODAL */}
        {modalOpen && (
          <div
            className="fixed inset-0 flex justify-center items-center bg-black/80 p-3 z-50"
            onClick={closeModal}
          >
            <div
              className="relative bg-white w-full max-w-4xl rounded-lg p-4 xs:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              {/* FIXED CLOSE BUTTON WITH HIGH z-index */}
              <button
                className="absolute top-2 right-2 text-white bg-black p-3 rounded-full size-10 text-lg leading-none z-[9999]"
                onClick={closeModal}
              >
                ✕
              </button>

              {/* VIDEO WRAPPER WITH LOWER z-index */}
              <div
                className="relative w-full h-0 z-[1]"
                style={{ paddingBottom: "56.25%" }}
              >
                <iframe
                  src={`${selectedVideo}?autoplay=1`}
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                ></iframe>
              </div>
            </div>
          </div>
        )}
      </div>
    </AthleteLayout>
  );
};

// BACK ARROW ICON
const ArrowLWhite = ({ onClick }) => (
  <svg
    onClick={onClick}
    className="hover:opacity-50 rotate-180 cursor-pointer"
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="14"
    viewBox="0 0 8 14"
    fill="none"
  >
    <path
      d="M1 13L7 7L1 1"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
